/**
 * Product Comparison Page — Web Component
 *
 * Manages product selection, sticky bar, scroll sync, variant switching,
 * cart actions, and dynamic card rendering for all comparison rows.
 */
if (!customElements.get('td-comparison-page')) {

  // ── Constants ──────────────────────────────────────────────
  const SELECTORS = {
    productsJson:     '[data-comparison-products]',
    selectorsBar:     '[data-selectors-bar]',
    selectorsInner:   '[data-selectors-inner]',
    columnsInner:     '[data-columns-inner]',
    scrollContainer:  '[data-product-scroll]',
    iconBank:         '[data-icon-bank]',
    emptySlot:        '[data-empty-slot]',
    addTrigger:       '[data-add-product-trigger]',
    productColumn:    '.td-comparison-column[data-product-id]',
    selectorBtn:      '.td-comparison-column__selector, .td-comparison-empty-slot__selector',
    placeholder:      '.td-comparison-empty-slot__placeholder, [data-add-product-trigger]',
    emptySlotSelector:'.td-comparison-empty-slot__selector',
    swatch:           '.td-comparison-column__swatches .color-swatch',
    atcBtn:           '.td-comparison-column__atc',
    galleryPrev:      '[data-gallery-prev]',
    galleryNext:      '[data-gallery-next]',
    galleryCounter:   '[data-gallery-counter]',
    unitToggle:       '[data-unit-toggle]',
    tooltipToggle:    '[data-strap-tooltip-toggle]',
    tooltipOpen:      '.td-comparison-strap-card__tooltip--open',
    removeProduct:    '[data-remove-product]',
    sizeSelect:       '[data-size-select]',
  };

  const CSS_CLASSES = {
    fixed:      'td-comparison-section__selectors--fixed',
    placeholder:'td-comparison-section__selectors-placeholder',
    placeholderActive: 'td-comparison-section__selectors-placeholder--active',
    unitActive: 'td-comparison-section__unit-btn--active',
    tooltipOpen:'td-comparison-strap-card__tooltip--open',
  };

  const STICKY_HYSTERESIS = 30;
  const ATC_FEEDBACK_MS   = 1500;
  const SCROLL_RESTORE_ATTEMPTS = 10;

  const COMPARISON_ROWS = {
    idealFor:    { inner: '[data-ideal-for-inner]',   cardSelector: '.td-comparison-section__ideal-card' },
    dimensions:  { inner: '[data-dimensions-inner]',  cardSelector: '.td-comparison-section__dimensions-card' },
    straps:      { inner: '[data-straps-inner]',      cardSelector: '.td-comparison-strap-card' },
    features:    { inner: '[data-features-inner]',    cardSelector: '.td-comparison-features-card' },
    materials:   { inner: '[data-materials-inner]',   cardSelector: '.td-comparison-materials-card' },
    compatible:  { inner: '[data-compatible-inner]',  cardSelector: '.td-comparison-compatible-card' },
    video:       { inner: '[data-video-inner]',       cardSelector: '.td-comparison-section__video-slot' },
  };

  // ── Helpers ────────────────────────────────────────────────

  const formatMoney = cents => { const val = cents / 100; return '$' + (val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)); };

  function sizedImageUrl(src, width) {
    if (!src) return '';
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src);
      url.searchParams.set('width', width);
      return url.toString();
    } catch { return src; }
  }

  function calcPricing(variant, discountPercent = 0) {
    if (discountPercent > 0) {
      return {
        comparePrice: formatMoney(variant.price),
        price: formatMoney(Math.round(variant.price * (100 - discountPercent) / 100)),
        hasCompare: true,
      };
    }
    if (variant.compare_at_price > variant.price) {
      return {
        comparePrice: formatMoney(variant.compare_at_price),
        price: formatMoney(variant.price),
        hasCompare: true,
      };
    }
    return { comparePrice: '', price: formatMoney(variant.price), hasCompare: false };
  }

  /** Find option index by name (handles both string[] and {name}[] formats) */
  function getOptionIndex(product, ...names) {
    if (!product.options) return -1;
    return product.options.findIndex(opt => {
      const name = (typeof opt === 'string' ? opt : opt.name || '').toLowerCase();
      return names.includes(name);
    });
  }

  /** Extract unique option values from variants at a given option index */
  function getUniqueOptionValues(product, optionIdx) {
    const values = [];
    if (optionIdx < 0 || !product.variants) return values;
    const seen = new Set();
    for (const v of product.variants) {
      const val = v.options?.[optionIdx];
      if (val && !seen.has(val)) {
        seen.add(val);
        values.push({ name: val, variantId: v.id, available: v.available });
      }
    }
    return values;
  }

  /** Find variant matching specific option values */
  function findVariant(product, colorIdx, color, sizeIdx, size) {
    return product.variants.find(v =>
      (colorIdx < 0 || v.options[colorIdx] === color) &&
      (sizeIdx < 0 || v.options[sizeIdx] === size)
    );
  }

  function getHeaderBottom() {
    const style = getComputedStyle(document.documentElement);
    return (parseInt(style.getPropertyValue('--header-height')) || 0)
         + (parseInt(style.getPropertyValue('--announcement-bar-height')) || 0);
  }

  /**
   * Get images for a specific color. Two strategies:
   * 1. Alt-tag filtering (#OptionName_Value) — matches PDP/Focal approach
   * 2. Position-based slicing — fallback using variant featured_image positions
   */
  function getImagesForColor(product, colorValue) {
    if (!product || !colorValue) return null;
    const colorIdx = getOptionIndex(product, 'color', 'colour');
    if (colorIdx < 0 || !product.images?.length) return null;

    // Strategy 1: Alt-tag filtering (if any media has '#' in alt)
    const media = product.media || [];
    const hasAltGroups = media.some(m => m.alt && m.alt.includes('#'));
    if (hasAltGroups) {
      const colorLower = colorValue.toLowerCase().trim();
      const filtered = [];
      for (const m of media) {
        if (m.media_type && m.media_type !== 'image') continue;
        if (m.alt && m.alt.includes('#')) {
          const groupParts = m.alt.split('#').pop().split('_');
          const optionName = groupParts.shift().toLowerCase().trim();
          const optionValue = groupParts.join('_').toLowerCase().trim();
          const optIdx = getOptionIndex(product, optionName);
          if (optIdx >= 0 && optionValue === colorLower) {
            filtered.push(m.src);
          }
        } else {
          // No '#' in alt — shared image, show for all colors
          if (m.src) filtered.push(m.src);
        }
      }
      if (filtered.length > 0) return filtered;
    }

    // Strategy 2: Position-based slicing using featured_image positions
    const colorPositions = [];
    const seenColors = new Set();
    for (const v of product.variants) {
      const color = v.options?.[colorIdx];
      if (color && !seenColors.has(color) && v.featured_image) {
        seenColors.add(color);
        colorPositions.push({ color, position: v.featured_image.position });
      }
    }
    if (colorPositions.length <= 1) return null;

    colorPositions.sort((a, b) => a.position - b.position);

    const targetIdx = colorPositions.findIndex(
      c => c.color.toLowerCase().trim() === colorValue.toLowerCase().trim()
    );
    if (targetIdx < 0) return null;

    const startPos = colorPositions[targetIdx].position - 1;
    const endPos = targetIdx < colorPositions.length - 1
      ? colorPositions[targetIdx + 1].position - 1
      : product.images.length;

    const sliced = product.images.slice(startPos, endPos);
    return sliced.length > 0 ? sliced : null;
  }

  /** Safely convert metafield value to string */
  function metaStr(v) {
    if (v == null) return '';
    if (typeof v === 'object') return v.value ?? '';
    return String(v);
  }

  /** Bind handler to elements matching selector, skip already-bound */
  function bindOnce(root, selector, flag, handler) {
    root.querySelectorAll(selector).forEach(el => {
      if (el[flag]) return;
      el[flag] = true;
      handler(el);
    });
  }

  // ── Component ──────────────────────────────────────────────

  class TDComparisonPage extends HTMLElement {

    // ── Lifecycle ──────────────────────────────────────────

    constructor() {
      super();
      this.products = [];
      this.maxProducts = parseInt(this.dataset.maxProducts) || 4;
      this.activeDropdown = null;
      this._productCache = {};
      this._iconCache = {};
      this._isBarFixed = false;
      this._onDocClick = this._onDocumentClick.bind(this);
    }

    connectedCallback() {
      this._parseProducts();
      this._parseLabels();
      this.selectorsBar   = this.querySelector(SELECTORS.selectorsBar);
      this.selectorsInner = this.querySelector(SELECTORS.selectorsInner);
      this.columnsInner   = this.querySelector(SELECTORS.columnsInner);

      this._bindSelectors();
      this._bindInteractions();
      this._updateSelectedCount();
      this._initStickyObserver();
      this._initVideoModalCleanup();
      this._prefetchProducts();
      this._initColumnGalleries();
      this._initDragScroll();

      document.addEventListener('click', this._onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll);
        document.body.style.paddingBottom = '';
        document.documentElement.style.overflowX = '';
      }
    }

    // ── Data ───────────────────────────────────────────────

    _parseProducts() {
      const el = this.querySelector(SELECTORS.productsJson);
      if (!el) return;
      try { this.products = JSON.parse(el.textContent); }
      catch (e) { console.error('Failed to parse comparison products JSON', e); }
    }

    _parseLabels() {
      const defaults = {
        atc_label: 'ADD TO CART', sold_out_label: 'SOLD OUT',
        adding_label: 'ADDING...', added_label: 'ADDED!',
        color_prefix: 'Color:', size_prefix: 'Size:',
        learn_more_label: 'Learn More', remove_label: 'Remove product',
        reviews_label: 'reviews',
        blocks: {
          ideal_for: { row_title: 'Trip Duration' },
          dimensions: { metric_label: 'Metric', imperial_label: 'Imperial', dimensions_label: 'Dimensions', weight_label: 'Weight' },
          straps: { shoulder_name: 'Shoulder', sternum_name: 'Sternum', accessory_name: 'Accessory', hip_belt_name: 'Hip Belt', accessory_tooltip_title: '', accessory_tooltip_body: '' },
          key_features: { tech_storage_label: 'Tech Storage:', pockets_label: 'Pockets:', load_support_label: 'Load Support:', handles_zippers_label: 'Handles + Zippers' },
          materials: { exterior_label: 'Exterior:', interior_label: 'Interior:', coating_label: 'Coating:', zippers_label: 'Zippers:' },
        }
      };
      const el = this.querySelector('[data-comparison-labels]');
      if (!el) { this.labels = defaults; return; }
      try {
        const parsed = JSON.parse(el.textContent);
        this.labels = { ...defaults, ...parsed, blocks: { ...defaults.blocks } };
        if (parsed.blocks) {
          for (const [key, val] of Object.entries(parsed.blocks)) {
            this.labels.blocks[key] = { ...(defaults.blocks[key] || {}), ...val };
          }
        }
      } catch (e) {
        console.error('Failed to parse comparison labels JSON', e);
        this.labels = defaults;
      }
    }

    get selectedIds() {
      const ids = new Set();
      this.querySelectorAll(SELECTORS.productColumn).forEach(col => {
        const id = parseInt(col.dataset.productId);
        if (id) ids.add(id);
      });
      return [...ids];
    }

    get selectedCount() { return this.selectedIds.length; }

    get availableProducts() {
      const selected = this.selectedIds;
      return this.products.filter(p => !selected.includes(p.id));
    }

    _findMeta(productId, handle) {
      return this.products.find(p =>
        (handle && p.handle === handle) ||
        (productId && String(p.id) === String(productId))
      );
    }

    async _getProduct(handle) {
      if (!handle) return null;
      const cached = this._productCache[handle];
      if (cached && !(cached instanceof Promise)) return cached;
      if (!cached) {
        this._productCache[handle] = fetch(`/products/${handle}.js`).then(r => r.json());
      }
      try {
        const product = await this._productCache[handle];
        this._productCache[handle] = product;
        return product;
      } catch (e) {
        console.error(`Failed to load product: ${handle}`, e);
        delete this._productCache[handle];
        return null;
      }
    }

    _prefetchProducts() {
      const handles = new Set();
      this.querySelectorAll(SELECTORS.productColumn).forEach(col =>
        handles.add(col.dataset.productHandle)
      );
      handles.forEach(h => this._getProduct(h));
    }

    async _initColumnGalleries() {
      const columns = this.querySelectorAll('.td-comparison-column--card-only');
      for (const column of columns) {
        const handle = column.dataset.productHandle;
        const product = await this._getProduct(handle);
        if (!product) continue;
        const colorIdx = getOptionIndex(product, 'color', 'colour');
        const checkedSwatch = column.querySelector('.color-swatch__radio:checked');
        const colorVal = checkedSwatch
          ? checkedSwatch.value
          : (colorIdx >= 0 ? product.variants[0].options[colorIdx] : null);
        column._activeImages = getImagesForColor(product, colorVal) || product.images || [];
      }
    }

    _initDragScroll() {
      const el = this.querySelector('[data-product-scroll]');
      if (!el) return;
      this._dragScrollEl = el;
      this._dragBarEl = this.querySelector(SELECTORS.selectorsBar);
      this._makeDraggable(el);
      if (this._dragBarEl) this._makeDraggable(this._dragBarEl, el);
      this._updateScrollable();
      const ro = new ResizeObserver(() => this._updateScrollable());
      ro.observe(el);
    }

    _updateScrollable() {
      const el = this._dragScrollEl;
      if (!el) return;
      const scrollable = el.scrollWidth > el.clientWidth;
      el.classList.toggle('is-scrollable', scrollable);
      if (this._dragBarEl) this._dragBarEl.classList.toggle('is-scrollable', scrollable);
    }

    _makeDraggable(el, scrollTarget) {
      const target = scrollTarget || el;
      let isDown = false, startX, scrollLeft;
      el.addEventListener('dragstart', (e) => { e.preventDefault(); });
      el.addEventListener('mousedown', (e) => {
        if (e.target.closest('button, a, input, select, label')) return;
        isDown = true;
        el.classList.add('is-dragging');
        startX = e.pageX - el.offsetLeft;
        scrollLeft = target.scrollLeft;
      });
      el.addEventListener('mouseleave', () => { isDown = false; el.classList.remove('is-dragging'); });
      el.addEventListener('mouseup', () => { isDown = false; el.classList.remove('is-dragging'); });
      el.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - el.offsetLeft;
        target.scrollLeft = scrollLeft - (x - startX);
      });
    }

    _getIcon(name) {
      if (this._iconCache[name] !== undefined) return this._iconCache[name];
      const bank = this.querySelector(SELECTORS.iconBank);
      const span = bank?.querySelector(`[data-icon="${name}"]`);
      this._iconCache[name] = span?.innerHTML ?? '';
      return this._iconCache[name];
    }

    _announce(msg) {
      const region = this.querySelector('[data-comparison-live]');
      if (region) { region.textContent = ''; requestAnimationFrame(() => { region.textContent = msg; }); }
    }

    // ── Sticky Bar ────────────────────────────────────────

    _initStickyObserver() {
      if (!this.selectorsBar) return;

      this._barPlaceholder = document.createElement('div');
      this._barPlaceholder.className = CSS_CLASSES.placeholder;
      this._barPlaceholder.setAttribute('aria-hidden', 'true');
      this.selectorsBar.after(this._barPlaceholder);
      this._barPlaceholder.style.height = `${this.selectorsBar.offsetHeight}px`;

      // Compute fixed thresholds once — stable regardless of layout shifts
      this._computeStickyThresholds();

      this._onScroll = () => {
        const scrollY = window.scrollY;
        const shouldFix = scrollY >= this._fixThreshold && scrollY < this._unfixBottomThreshold;

        if (shouldFix && !this._isBarFixed) this._fixBar();
        else if (!shouldFix && this._isBarFixed) this._unfixBar();
      };

      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    _computeStickyThresholds() {
      const headerBottom = getHeaderBottom();
      // Use placeholder when bar is fixed (bar is position:fixed, out of flow)
      const ref = this._isBarFixed && this._barPlaceholder ? this._barPlaceholder : this.selectorsBar;
      const refRect = ref.getBoundingClientRect();
      const barHeight = this.selectorsBar.offsetHeight;
      // Fix when the bar's bottom edge reaches the header
      this._fixThreshold = window.scrollY + refRect.bottom - headerBottom;
      // Unfix when the section bottom scrolls past the fixed bar position
      const sectionRect = this.getBoundingClientRect();
      this._unfixBottomThreshold = window.scrollY + sectionRect.bottom - headerBottom - barHeight;
    }

    _fixBar() {
      this._closeDropdown();
      this._isBarFixed = true;
      this._barPlaceholder.style.height = `${this.selectorsBar.offsetHeight}px`;
      this._barPlaceholder.classList.add(CSS_CLASSES.placeholderActive);
      this.selectorsBar.classList.add(CSS_CLASSES.fixed);
      document.documentElement.style.overflowX = 'hidden';
      this._startScrollSync();
      requestAnimationFrame(() => {
        document.body.style.paddingBottom = `${this.selectorsBar.offsetHeight}px`;
        const gorgiasBtn = document.querySelector('#chat-button');
        if (gorgiasBtn) {
          gorgiasBtn.style.bottom = `${this.selectorsBar.offsetHeight + 23}px`;
        } else {
          this._watchForGorgias();
        }
      });
    }

    _unfixBar() {
      this._isBarFixed = false;
      const savedScroll = this._scrollContainer
        ? this._scrollContainer.scrollLeft
        : this.selectorsBar.scrollLeft;
      this._stopScrollSync();
      this._closeDropdown();

      this.selectorsBar.classList.remove(CSS_CLASSES.fixed);
      this._barPlaceholder.classList.remove(CSS_CLASSES.placeholderActive);
      document.body.style.paddingBottom = '';
      document.documentElement.style.overflowX = '';
      if (this._gorgiasObserver) {
        this._gorgiasObserver.disconnect();
        this._gorgiasObserver = null;
      }
      const gorgiasBtn = document.querySelector('#chat-button');
      if (gorgiasBtn) gorgiasBtn.style.bottom = '';

      // Restore horizontal scroll (skip animation if reduced motion preferred)
      const container = this.querySelector(SELECTORS.scrollContainer);
      if (container && savedScroll > 0) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          container.scrollLeft = savedScroll;
        } else {
          let attempts = 0;
          const restore = () => {
            container.scrollLeft = savedScroll;
            if (++attempts < SCROLL_RESTORE_ATTEMPTS &&
                Math.abs(container.scrollLeft - savedScroll) > 1) {
              requestAnimationFrame(restore);
            }
          };
          requestAnimationFrame(restore);
        }
      }
    }


    // ── Gorgias Chat Repositioning ──────────────────────────

    _watchForGorgias() {
      if (this._gorgiasObserver) return;
      this._gorgiasObserver = new MutationObserver(() => {
        const btn = document.querySelector('#chat-button');
        if (btn && this._isBarFixed) {
          btn.style.bottom = `${this.selectorsBar.offsetHeight + 23}px`;
          this._gorgiasObserver.disconnect();
          this._gorgiasObserver = null;
        }
      });
      this._gorgiasObserver.observe(document.body, { childList: true, subtree: true });
    }

    // ── Scroll Sync ───────────────────────────────────────

    _startScrollSync() {
      this._scrollContainer = this.querySelector(SELECTORS.scrollContainer);
      if (!this._scrollContainer) return;

      if (CSS.supports('animation-timeline', 'scroll()')) {
        // Compositor-driven: set max offset, CSS handles the rest
        this._updateMaxScroll();
        this._syncRo = new ResizeObserver(() => this._updateMaxScroll());
        this._syncRo.observe(this._scrollContainer);
        this._cssSync = true;
      } else {
        // JS fallback: transform sync + pointer-events passthrough for native momentum
        this._selectorsInner = this.selectorsBar.querySelector(SELECTORS.selectorsInner);
        if (!this._selectorsInner) return;

        this.selectorsBar.style.overflow = 'hidden';
        this.selectorsBar.style.pointerEvents = 'none';
        this._selectorsInner.style.willChange = 'transform';

        // Let interactive children remain tappable
        this.selectorsBar.querySelectorAll(SELECTORS.selectorBtn + ', ' + SELECTORS.placeholder).forEach(el => {
          el.style.pointerEvents = 'auto';
        });

        // Extend scroll container behind bar so touches pass through
        const barH = this.selectorsBar.offsetHeight;
        this._scrollContainer.style.paddingBottom = `${barH}px`;

        this._onContainerScroll = () => {
          this._selectorsInner.style.transform = `translateX(-${this._scrollContainer.scrollLeft}px)`;
        };
        this._scrollContainer.addEventListener('scroll', this._onContainerScroll, { passive: true });
        this._onContainerScroll();
        this._jsSync = true;
      }

      // Both paths: forward trackpad/mouse wheel on the bar to the scroll container
      this._onBarWheel = (e) => {
        if (e.deltaX === 0) return;
        e.preventDefault();
        this._scrollContainer.scrollLeft += e.deltaX;
      };
      this.selectorsBar.addEventListener('wheel', this._onBarWheel, { passive: false });
    }

    _updateMaxScroll() {
      if (!this._scrollContainer) return;
      const max = this._scrollContainer.scrollWidth - this._scrollContainer.clientWidth;
      this.style.setProperty('--td-max-scroll', `-${max}px`);
    }

    _stopScrollSync() {
      if (this._cssSync) {
        this._syncRo?.disconnect();
        this._syncRo = null;
        this.style.removeProperty('--td-max-scroll');
        this._cssSync = false;
      } else if (this._jsSync) {
        this._scrollContainer?.removeEventListener('scroll', this._onContainerScroll);
        if (this._selectorsInner) {
          this._selectorsInner.style.transform = '';
          this._selectorsInner.style.willChange = '';
        }
        if (this.selectorsBar) {
          this.selectorsBar.style.overflow = '';
          this.selectorsBar.style.pointerEvents = '';
          this.selectorsBar.querySelectorAll(SELECTORS.selectorBtn + ', ' + SELECTORS.placeholder).forEach(el => {
            el.style.pointerEvents = '';
          });
        }
        if (this._scrollContainer) this._scrollContainer.style.paddingBottom = '';
        this._onContainerScroll = null;
        this._selectorsInner = null;
        this._jsSync = false;
      }
      this.selectorsBar?.removeEventListener('wheel', this._onBarWheel);
      this._onBarWheel = null;
      this._scrollContainer = null;
    }

    // ── Event Binding ─────────────────────────────────────

    _bindSelectors() {
      bindOnce(this, SELECTORS.selectorBtn, '_bound', el => {
        el.addEventListener('click', e => { e.stopPropagation(); this._toggleDropdown(el); });
      });
      bindOnce(this, SELECTORS.placeholder, '_bound', el => {
        el.addEventListener('click', e => {
          e.stopPropagation();
          const btn = this.querySelector(SELECTORS.emptySlotSelector);
          if (btn) this._toggleDropdown(btn);
        });
      });
    }

    _bindInteractions() {
      bindOnce(this, SELECTORS.swatch, '_iBound', el => {
        el.addEventListener('click', e => { e.preventDefault(); this._onSwatchClick(el); });
      });
      bindOnce(this, SELECTORS.atcBtn, '_iBound', el => {
        el.addEventListener('click', () => this._onAddToCart(el));
      });
      bindOnce(this, SELECTORS.galleryPrev, '_iBound', el => {
        el.addEventListener('click', () => this._onGalleryNav(el, -1));
      });
      bindOnce(this, SELECTORS.galleryNext, '_iBound', el => {
        el.addEventListener('click', () => this._onGalleryNav(el, 1));
      });
      bindOnce(this, SELECTORS.sizeSelect, '_iBound', el => {
        el.addEventListener('change', (e) => {
          if (e.target.classList.contains('td-comparison-column__size-radio')) this._onSizeChange(e.target);
        });
      });
      bindOnce(this, SELECTORS.unitToggle, '_iBound', el => {
        el.addEventListener('click', e => {
          const btn = e.target.closest('[data-unit]');
          if (btn) this._onUnitToggle(btn.dataset.unit, el);
        });
      });
      if (!this._tooltipDelegated) {
        this._tooltipDelegated = true;
        this.addEventListener('click', e => {
          const trigger = e.target.closest(SELECTORS.tooltipToggle);
          if (!trigger) return;
          e.stopPropagation();
          const tooltip = trigger.closest('.td-comparison-strap-card__tooltip-wrap')
            ?.querySelector('[data-strap-tooltip]');
          if (!tooltip) return;
          const wasOpen = tooltip.classList.contains(CSS_CLASSES.tooltipOpen);
          this._closeAllTooltips();
          if (!wasOpen) tooltip.classList.add(CSS_CLASSES.tooltipOpen);
        });
        this.addEventListener('keydown', e => {
          if (e.key === 'Escape') this._closeAllTooltips();
        });
      }
      bindOnce(this, '[data-video-id]', '_iBound', el => {
        el.addEventListener('click', () => {
          const iframe = document.querySelector('#td-comparison-video-popup iframe');
          if (iframe && el.dataset.videoId) {
            const src = `https://www.youtube.com/embed/${el.dataset.videoId}?playsinline=1&autoplay=1&controls=0&loop=1&rel=0&modestbranding=0`;
            iframe.setAttribute('data-video-src', src);
            iframe.setAttribute('src', src);
          }
        });
      });
      bindOnce(this, SELECTORS.removeProduct, '_iBound', el => {
        el.addEventListener('click', () => this._removeProduct(el));
      });
    }

    _initVideoModalCleanup() {
      const modal = document.getElementById('td-comparison-video-popup');
      if (!modal) return;
      const clearIframe = () => {
        const iframe = modal.querySelector('iframe');
        if (iframe) iframe.setAttribute('src', '');
      };
      modal.querySelector('.modal__close-button')?.addEventListener('click', clearIframe);
      modal.querySelector('.modal__overlay')?.addEventListener('click', clearIframe);
    }

    // ── Dropdown ──────────────────────────────────────────

    _toggleDropdown(btn) {
      const wasOpen = this.activeDropdown === btn;
      this._closeDropdown();
      if (wasOpen) return;

      const available = this.availableProducts;
      if (!available.length) return;

      const dropdownId = `comparison-dropdown-${Date.now()}`;
      const dropdown = document.createElement('div');
      dropdown.className = 'td-comparison-dropdown';
      dropdown.id = dropdownId;
      dropdown.setAttribute('role', 'listbox');
      dropdown.innerHTML = available.map(p => `
        <button class="td-comparison-dropdown__item" type="button" role="option" data-handle="${p.handle}" data-product-id="${p.id}" tabindex="-1">
          <img class="td-comparison-dropdown__image" src="${p.featured_image}" alt="${p.title}" width="40" height="40" loading="lazy">
          <div class="td-comparison-dropdown__info">
            <span class="td-comparison-dropdown__name">${p.title}</span>
            ${p.volume ? `<span class="td-comparison-dropdown__volume">${p.volume}</span>` : ''}
          </div>
        </button>`).join('');

      const column = btn.closest('.td-comparison-column, .td-comparison-empty-slot');
      const isInFixedBar = this.selectorsBar?.classList.contains(CSS_CLASSES.fixed)
        && this.selectorsBar.contains(btn);

      if (isInFixedBar) {
        const rect = btn.getBoundingClientRect();
        Object.assign(dropdown.style, {
          position: 'fixed', top: 'auto',
          bottom: `${window.innerHeight - rect.top + 4}px`,
          left: `${rect.left}px`, width: `${rect.width}px`, zIndex: '6'
        });
        document.body.appendChild(dropdown);
      } else {
        column.style.position = 'relative';
        column.appendChild(dropdown);
      }

      dropdown.querySelectorAll('.td-comparison-dropdown__item').forEach(item => {
        item.addEventListener('click', e => {
          e.stopPropagation();
          this._selectProduct(item.dataset.handle, column);
        });
      });

      // Keyboard navigation
      dropdown.addEventListener('keydown', e => this._onDropdownKeydown(e, column));
      btn.addEventListener('keydown', e => {
        if (e.key === 'ArrowDown') { e.preventDefault(); dropdown.querySelector('.td-comparison-dropdown__item')?.focus(); }
        if (e.key === 'Escape') this._closeDropdown();
      }, { once: true });

      btn.setAttribute('aria-expanded', 'true');
      btn.setAttribute('aria-controls', dropdownId);
      this.activeDropdown = btn;
      this.activeDropdownEl = dropdown;

      // Focus first item
      requestAnimationFrame(() => dropdown.querySelector('.td-comparison-dropdown__item')?.focus());
    }

    _onDropdownKeydown(e, column) {
      const items = [...this.activeDropdownEl.querySelectorAll('.td-comparison-dropdown__item')];
      const idx = items.indexOf(document.activeElement);
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          items[(idx + 1) % items.length]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          items[(idx - 1 + items.length) % items.length]?.focus();
          break;
        case 'Escape':
          e.preventDefault();
          this._closeDropdown();
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (idx >= 0) this._selectProduct(items[idx].dataset.handle, column);
          break;
      }
    }

    _closeDropdown() {
      const trigger = this.activeDropdown;
      this.activeDropdownEl?.remove();
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.removeAttribute('aria-controls');
        trigger.focus();
      }
      this.activeDropdown = this.activeDropdownEl = null;
    }

    _closeAllTooltips() {
      this.querySelectorAll(SELECTORS.tooltipOpen).forEach(t =>
        t.classList.remove(CSS_CLASSES.tooltipOpen)
      );
    }

    _onDocumentClick() {
      this._closeDropdown();
      this._closeAllTooltips();
    }

    // ── Product Selection ─────────────────────────────────

    async _selectProduct(handle, column) {
      this._closeDropdown();
      if (column.classList.contains('td-comparison-empty-slot') && this.selectedCount >= this.maxProducts) return;
      const meta = this.products.find(p => p.handle === handle);
      if (!meta) return;
      const fullProduct = await this._getProduct(handle);
      if (!fullProduct) return;

      if (column.classList.contains('td-comparison-empty-slot')) {
        this._addProduct(fullProduct, meta, column);
      } else {
        this._swapProduct(fullProduct, meta, column, !!column.closest('[data-selectors-inner]'));
      }

      // Initialize variant-specific gallery for the new column
      const newCol = this.columnsInner?.querySelector(`.td-comparison-column[data-product-id="${fullProduct.id}"]`);
      if (newCol) {
        const colorIdx = getOptionIndex(fullProduct, 'color', 'colour');
        const colorVal = colorIdx >= 0 ? fullProduct.variants[0].options[colorIdx] : null;
        newCol._activeImages = getImagesForColor(fullProduct, colorVal) || fullProduct.images || [];
      }

      this._bindSelectors();
      this._bindInteractions();
      this._updateSelectedCount();
      this._updateEmptySlotVisibility();
      this._announce(`${fullProduct.title} added to comparison`);
    }

    _addProduct(fullProduct, meta, emptySlotEl) {
      const inSelectors = !!emptySlotEl.closest('[data-selectors-inner]');

      if (inSelectors) {
        emptySlotEl.insertAdjacentHTML('beforebegin', this._buildSelectorHTML(fullProduct, meta));
        const cardSlot = this.columnsInner?.querySelector(SELECTORS.emptySlot);
        if (cardSlot) cardSlot.insertAdjacentHTML('beforebegin', this._buildCardHTML(fullProduct, meta));
      } else {
        const selectorSlot = this.selectorsInner?.querySelector(SELECTORS.emptySlot);
        if (selectorSlot) selectorSlot.insertAdjacentHTML('beforebegin', this._buildSelectorHTML(fullProduct, meta));
        emptySlotEl.insertAdjacentHTML('beforebegin', this._buildCardHTML(fullProduct, meta));
      }

      for (const [key, { inner }] of Object.entries(COMPARISON_ROWS)) {
        try {
          const html = this._buildRowCardHTML(key, meta);
          if (html) this._addRowCard(inner, html);
        } catch (e) { console.error(`Failed to add ${key} card`, e); }
      }

      this._updateEmptySlotVisibility();
    }

    _swapProduct(fullProduct, meta, column, isInSelectorBar) {
      const oldId = column.dataset.productId;

      if (isInSelectorBar) {
        column.outerHTML = this._buildSelectorHTML(fullProduct, meta);
        const card = this.columnsInner?.querySelector(`.td-comparison-column[data-product-id="${oldId}"]`);
        if (card) card.outerHTML = this._buildCardHTML(fullProduct, meta);
      } else {
        column.outerHTML = this._buildCardHTML(fullProduct, meta);
        const sel = this.selectorsInner?.querySelector(`.td-comparison-column[data-product-id="${oldId}"]`);
        if (sel) sel.outerHTML = this._buildSelectorHTML(fullProduct, meta);
      }

      for (const [key, { cardSelector }] of Object.entries(COMPARISON_ROWS)) {
        try {
          const html = this._buildRowCardHTML(key, meta);
          if (html) this._swapRowCard(cardSelector, oldId, html);
        } catch (e) { console.error(`Failed to swap ${key} card`, e); }
      }
    }

    _addRowCard(rowSelector, html) {
      const row = this.querySelector(rowSelector);
      if (!row) return;
      const slot = row.querySelector('.td-comparison-section__ideal-empty, [data-add-product-trigger]');
      slot ? slot.insertAdjacentHTML('beforebegin', html) : row.insertAdjacentHTML('beforeend', html);
    }

    _swapRowCard(selector, oldProductId, newHTML) {
      const old = this.querySelector(`${selector}[data-product-id="${oldProductId}"]`);
      if (old) old.outerHTML = newHTML;
    }

    _updateSelectedCount() { this.dataset.selectedCount = this.selectedCount; this._updateScrollable(); }

    _removeProduct(btn) {
      const column = btn.closest('.td-comparison-column');
      if (!column) return;
      const productId = column.dataset.productId;
      const meta = this._findMeta(productId);
      const productTitle = meta?.title || 'Product';

      this.querySelectorAll(`.td-comparison-column[data-product-id="${productId}"]`).forEach(el => el.remove());

      for (const { cardSelector } of Object.values(COMPARISON_ROWS)) {
        this.querySelector(`${cardSelector}[data-product-id="${productId}"]`)?.remove();
      }

      this._updateSelectedCount();
      this._updateEmptySlotVisibility();
      this._announce(`${productTitle} removed from comparison`);
    }

    _updateEmptySlotVisibility() {
      const hide = this.selectedCount >= this.maxProducts;
      this.querySelectorAll(`${SELECTORS.emptySlot}, ${SELECTORS.addTrigger}`).forEach(el => {
        el.style.display = hide ? 'none' : '';
      });
      // Section height changed — recompute sticky thresholds
      requestAnimationFrame(() => this._computeStickyThresholds?.());
    }

    // ── Variant Resolution ────────────────────────────────

    /**
     * Resolve the correct variant based on current color + size selections,
     * update pricing/ATC/dimensions across card and sticky bar.
     */
    async _resolveAndUpdateVariant(column, product, { newColor, newSize } = {}) {
      const colorIdx = getOptionIndex(product, 'color', 'colour');
      const sizeIdx = getOptionIndex(product, 'size');
      const sizeWrap = column.querySelector(SELECTORS.sizeSelect);
      const checkedSwatch = column.querySelector(`${SELECTORS.swatch} .color-swatch__radio:checked`);
      const checkedSize = sizeWrap?.querySelector('.td-comparison-column__size-radio:checked');

      const color = newColor ?? checkedSwatch?.value ?? null;
      let size = newSize;
      if (!size && checkedSize) {
        size = checkedSize.dataset.sizeValue || null;
      }

      const variant = findVariant(product, colorIdx, color, sizeIdx, size)
        || product.variants.find(v => v.id === parseInt(checkedSize?.value))
        || product.variants[0];

      // Update size swatch states for new color
      if (newColor && sizeWrap && sizeIdx >= 0) {
        sizeWrap.querySelectorAll('.td-comparison-column__size-radio').forEach(radio => {
          const sizeVal = radio.dataset.sizeValue;
          const v = findVariant(product, colorIdx, newColor, sizeIdx, sizeVal);
          if (v) { radio.value = v.id; radio.disabled = !v.available; }
        });
      }

      const { productId, productHandle } = column.dataset;
      const meta = this._findMeta(productId, productHandle);
      const discount = meta?.discount_percent || parseInt(column.dataset.discountPercent || '0');

      this._updateColumnVariant(column, variant, discount);
      const barCol = this.selectorsInner?.querySelector(`.td-comparison-column[data-product-id="${productId}"]`);
      if (barCol) this._updateColumnVariant(barCol, variant, discount);

      const dimData = meta?.variants_dimensions?.[String(variant.id)];
      if (dimData) this._updateDimensionsCard(productId, dimData);

      return variant;
    }

    // ── Swatch + Size Handlers ────────────────────────────

    async _onSwatchClick(swatch) {
      const column = swatch.closest('.td-comparison-column');
      const product = await this._getProduct(column.dataset.productHandle);
      if (!product) return;

      const colorIdx = getOptionIndex(product, 'color', 'colour');
      const swatchVariant = product.variants.find(v => v.id === parseInt(swatch.dataset.variantId));
      if (!swatchVariant) return;
      const newColor = colorIdx >= 0 ? swatchVariant.options[colorIdx] : null;

      // Update radio state
      column.querySelectorAll(SELECTORS.swatch).forEach(s => {
        const r = s.querySelector('.color-swatch__radio');
        if (r) r.checked = (s === swatch);
      });

      // Update color label
      const label = column.querySelector('.td-comparison-column__color-label');
      if (label && newColor) label.textContent = `Color: ${newColor}`;

      await this._resolveAndUpdateVariant(column, product, { newColor });

      // Get images for this color (sliced by featured_image positions)
      const colorImages = getImagesForColor(product, newColor);
      column._activeImages = colorImages || product.images || [];

      // Show first image from the active set
      column._galleryIndex = 0;
      const img = column.querySelector('.td-comparison-column__image');
      if (img && column._activeImages.length > 0) {
        img.removeAttribute('srcset');
        img.src = sizedImageUrl(column._activeImages[0], 600);
      }
      this._updateGalleryCounter(column);
    }

    async _onSizeChange(radio) {
      const column = radio.closest('.td-comparison-column');
      const product = await this._getProduct(column.dataset.productHandle);
      if (!product) return;

      const newSize = radio.dataset.sizeValue;
      await this._resolveAndUpdateVariant(column, product, { newSize });
    }

    _updateColumnVariant(column, variant, discountPercent = 0) {
      const pricing = column.querySelector('.td-comparison-column__pricing');
      if (pricing) {
        let compareEl = pricing.querySelector('.td-comparison-column__price-compare');
        const priceEl = pricing.querySelector('.td-comparison-column__price');
        const { comparePrice, price, hasCompare } = calcPricing(variant, discountPercent);

        if (hasCompare) {
          if (compareEl) compareEl.textContent = comparePrice;
          else priceEl?.insertAdjacentHTML('beforebegin',
            `<p class="td-comparison-column__price-compare">${comparePrice}</p>`);
          if (priceEl) priceEl.textContent = price;
        } else {
          compareEl?.remove();
          if (priceEl) priceEl.textContent = price;
        }
      }

      const atc = column.querySelector(SELECTORS.atcBtn);
      if (atc) {
        atc.dataset.variantId = variant.id;
        atc.disabled = !variant.available;
        atc.textContent = variant.available ? this.labels.atc_label : this.labels.sold_out_label;
      }
    }

    _updateDimensionsCard(productId, dimData) {
      const card = this.querySelector(`.td-comparison-section__dimensions-card[data-product-id="${productId}"]`);
      if (!card) return;
      const blocks = card.querySelectorAll('.td-comparison-section__dimensions-block');
      [
        { block: blocks[0], metric: dimData.metric_dimensions, imperial: dimData.imperial_dimensions },
        { block: blocks[1], metric: dimData.metric_weight,     imperial: dimData.imperial_weight },
      ].forEach(({ block, metric, imperial }) => {
        if (!block) return;
        const m = block.querySelector('[data-unit-metric]');
        const i = block.querySelector('[data-unit-imperial]');
        if (m) m.textContent = metric || '';
        if (i) i.textContent = imperial || '';
      });
    }

    // ── Add to Cart ───────────────────────────────────────

    async _onAddToCart(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId || btn.disabled) return;

      const originalText = btn.textContent;
      btn.textContent = this.labels.adding_label;
      btn.disabled = true;
      this._announce(this.labels.adding_label);

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', 'mini-cart');

        const cartAddUrl = window.themeVariables?.routes?.cartAddUrl || '/cart/add';
        const res = await fetch(`${cartAddUrl}.js`, {
          method: 'POST', body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!res.ok) throw new Error(`Add to cart failed: ${res.status}`);
        const data = await res.json();
        btn.textContent = this.labels.added_label;
        this._announce(this.labels.added_label);

        const addedVariant = data.items?.[0] ?? data;
        btn.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: addedVariant } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: data, openMiniCart: window.themeVariables?.settings?.cartType === 'drawer' }
        }));

        fetch(`${window.themeVariables?.routes?.cartUrl || '/cart'}.js`)
          .then(r => r.json())
          .then(cart => document.documentElement.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })
          ));

        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, ATC_FEEDBACK_MS);
      } catch (e) {
        console.error('Add to cart error', e);
        btn.textContent = originalText;
        btn.disabled = false;
      }
    }

    // ── Unit Toggle ───────────────────────────────────────

    _onUnitToggle(unit, toggle) {
      const card = toggle.closest('.td-comparison-section__dimensions-card');
      if (!card) return;
      toggle.querySelectorAll('[data-unit]').forEach(b =>
        b.classList.toggle(CSS_CLASSES.unitActive, b.dataset.unit === unit)
      );
      const showMetric = unit === 'metric';
      card.querySelectorAll('[data-unit-metric]').forEach(el => { el.style.display = showMetric ? '' : 'none'; });
      card.querySelectorAll('[data-unit-imperial]').forEach(el => { el.style.display = showMetric ? 'none' : ''; });
    }

    // ── Gallery ───────────────────────────────────────────

    async _onGalleryNav(btn, direction) {
      const column = btn.closest('.td-comparison-column');
      const product = await this._getProduct(column.dataset.productHandle);
      if (!product) return;

      // Use active images if set, otherwise initialize by color position slicing
      if (!column._activeImages) {
        const colorIdx = getOptionIndex(product, 'color', 'colour');
        const checkedSwatch = column.querySelector('.color-swatch__radio:checked');
        const colorVal = checkedSwatch ? checkedSwatch.value : (colorIdx >= 0 ? product.variants[0].options[colorIdx] : null);
        column._activeImages = getImagesForColor(product, colorVal) || product.images || [];
      }
      const images = column._activeImages;
      if (images.length <= 1) return;

      column._galleryIndex = column._galleryIndex ?? 0;
      column._galleryIndex = (column._galleryIndex + direction + images.length) % images.length;

      const img = column.querySelector('.td-comparison-column__image');
      if (img) {
        img.removeAttribute('srcset');
        img.src = sizedImageUrl(images[column._galleryIndex], 600);
        img.alt = `${product.title}, image ${column._galleryIndex + 1} of ${images.length}`;
      }
      this._updateGalleryCounter(column);
    }

    _updateGalleryCounter(column) {
      const product = this._productCache[column.dataset.productHandle];
      if (!product || product instanceof Promise) return;
      const images = column._activeImages || product.images || [];
      const total = images.length || 1;
      const counter = column.querySelector(SELECTORS.galleryCounter);
      if (counter) counter.textContent = `${(column._galleryIndex || 0) + 1}/${total}`;
    }

    // ── HTML Builders ─────────────────────────────────────

    _buildSelectorHTML(product, meta) {
      const { comparePrice, price, hasCompare } = calcPricing(product.variants[0], meta.discount_percent || 0);
      return `
        <div class="td-comparison-column td-comparison-column--bar-item" data-product-id="${product.id}" data-product-handle="${product.handle}">
          <button class="td-comparison-column__selector" type="button" aria-expanded="false">
            <div class="td-comparison-column__selector-content">
              <span class="td-comparison-column__selector-name">${product.title}</span>
              ${meta.volume ? `<span class="td-comparison-column__selector-divider"></span><span class="td-comparison-column__selector-volume">${meta.volume}</span>` : ''}
            </div>
            ${this._getIcon('chevron-down') || '<svg width="20" height="20" class="icon icon--chevron-down" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="#FF5740" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>'}
          </button>
          <button type="button" class="td-comparison-column__remove" data-remove-product>${this.labels.remove_label}</button>
          <div class="td-comparison-column__bar-footer">
            <div class="td-comparison-column__pricing">
              ${hasCompare ? `<span class="visually-hidden">Original price:</span><p class="td-comparison-column__price-compare">${comparePrice}</p>` : ''}
              ${hasCompare ? '<span class="visually-hidden">Sale price:</span>' : ''}<p class="td-comparison-column__price">${price}</p>
            </div>
            <button class="td-comparison-column__atc td-comparison-column__atc--sale"
              type="button" data-variant-id="${product.variants[0].id}" ${!product.variants[0].available ? 'disabled' : ''}>
              ${product.variants[0].available ? this.labels.atc_label : this.labels.sold_out_label}
            </button>
          </div>
        </div>`;
    }

    _buildCardHTML(product, meta) {
      const variant = product.variants[0];
      const { comparePrice, price, hasCompare } = calcPricing(variant, meta.discount_percent || 0);
      const colorIdx = getOptionIndex(product, 'color', 'colour');
      const colorVal = colorIdx >= 0 ? variant.options[colorIdx] : null;
      const colorImages = getImagesForColor(product, colorVal);
      const activeImages = colorImages || product.images || [];
      const mediaCount = activeImages.length;
      const featuredImage = colorImages?.[0] || variant.featured_image?.src || product.featured_image || product.images?.[0] || '';
      const colorValues = getUniqueOptionValues(product, colorIdx);
      const swatches = meta.color_swatches || {};
      const swatchesHTML = colorValues.map((c, i) => {
        const id = `comparison-swatch-${product.id}-js-${i}`;
        const style = swatches[c.name] || `background-color: ${c.name.toLowerCase()}`;
        return `<div class="color-swatch" data-variant-id="${c.variantId}">
          <input class="color-swatch__radio visually-hidden" type="radio" name="color-${product.id}" id="${id}" value="${c.name}" ${i === 0 ? 'checked' : ''}>
          <label class="color-swatch__item" for="${id}" style="${style}"><span class="visually-hidden">${c.name}</span></label>
        </div>`;
      }).join('');

      const sizeIdx = getOptionIndex(product, 'size');
      const sizeValues = getUniqueOptionValues(product, sizeIdx);
      return `
        <div class="td-comparison-column td-comparison-column--card-only"
          data-product-id="${product.id}" data-product-handle="${product.handle}" data-discount-percent="${meta.discount_percent || 0}">
          <div class="td-comparison-column__card">
            <div class="td-comparison-column__options">
              <div class="td-comparison-column__options-left">
                <div class="td-comparison-column__color-info">
                  ${colorValues.length ? `<p class="td-comparison-column__color-label">Color: ${colorValues[0].name}</p>
                  <div class="color-swatch-list td-comparison-column__swatches">${swatchesHTML}</div>` : ''}
                </div>
                <div class="td-comparison-column__size-swatches-wrap">
                  ${sizeValues.length > 1 ? `<span class="td-comparison-column__size-label">${this.labels.size_prefix}</span>
                  <div class="td-comparison-column__size-swatch-list" data-size-select>
                    ${sizeValues.map((s, i) => `<div class="td-comparison-column__size-swatch">
                      <input class="td-comparison-column__size-radio visually-hidden" type="radio" name="size-${product.id}" id="size-${product.id}-${i + 1}" value="${s.variantId}" ${i === 0 ? 'checked' : ''} ${!s.available ? 'disabled' : ''} data-size-value="${s.name}">
                      <label class="td-comparison-column__size-swatch-item" for="size-${product.id}-${i + 1}">${s.name}</label>
                    </div>`).join('')}
                  </div>` : (sizeValues.length === 1 || meta.variant_size) ? `<span class="td-comparison-column__size-label">${this.labels.size_prefix}</span>
                  <div class="td-comparison-column__size-swatch-list">
                    <div class="td-comparison-column__size-swatch">
                      <input class="td-comparison-column__size-radio visually-hidden" type="radio" name="size-${product.id}" id="size-${product.id}-single" value="${variant.id}" checked>
                      <label class="td-comparison-column__size-swatch-item" for="size-${product.id}-single">${sizeValues.length === 1 ? sizeValues[0].name : meta.variant_size}</label>
                    </div>
                  </div>` : ''}
                  <div class="td-comparison-column__gallery-nav">
                    <button class="td-comparison-column__gallery-btn" type="button" aria-label="Previous image" data-gallery-prev>
                      <svg focusable="false" width="6" height="12" viewBox="0 0 8 12"><path fill="none" d="M7 1L2 6L7 11" stroke="currentColor" stroke-width="2"></path></svg>
                    </button>
                    <span class="td-comparison-column__gallery-counter" data-gallery-counter>1/${mediaCount || 1}</span>
                    <button class="td-comparison-column__gallery-btn" type="button" aria-label="Next image" data-gallery-next>
                      <svg focusable="false" width="6" height="12" viewBox="0 0 7 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.9394 5L0.469727 1.53033L1.53039 0.469666L6.06072 5L1.53039 9.53032L0.469727 8.46967L3.9394 5Z" fill="currentColor"></path></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="td-comparison-column__image-container" data-gallery-images>
              ${featuredImage ? `<img class="td-comparison-column__image" src="${featuredImage}" alt="${product.title}" loading="lazy">` : ''}
            </div>
            <div class="td-comparison-column__bottom">
              <p class="td-comparison-column__description">${meta.description || ''}</p>
              <div class="td-comparison-column__footer">
                <div class="td-comparison-column__pricing">
                  ${hasCompare ? `<span class="visually-hidden">Original price:</span><p class="td-comparison-column__price-compare">${comparePrice}</p>` : ''}
                  ${hasCompare ? '<span class="visually-hidden">Sale price:</span>' : ''}<p class="td-comparison-column__price">${price}</p>
                </div>
                <div class="td-comparison-column__atc-wrap">
                  <button class="td-comparison-column__atc td-comparison-column__atc--sale"
                    type="button" data-variant-id="${variant.id}" ${!variant.available ? 'disabled' : ''}>
                    ${variant.available ? this.labels.atc_label : this.labels.sold_out_label}
                  </button>
                  <a href="/products/${product.handle}" class="td-comparison-column__learn-more">${this.labels.learn_more_label}</a>
                </div>
              </div>
            </div>
          </div>
        </div>`;
    }

    // ── Row Card Builders ─────────────────────────────────

    _buildRowCardHTML(rowKey, meta) {
      return this[`_build_${rowKey}`]?.(meta) ?? '';
    }

    _build_idealFor(meta) {
      return `<div class="td-comparison-section__ideal-card" data-product-id="${meta.id}">
        <div class="td-comparison-section__ideal-content">
          <div class="td-comparison-section__ideal-icon-title">
            ${this._getIcon('calendar')}
            <div class="td-comparison-section__ideal-text">
              <p class="td-comparison-section__ideal-heading">Trip Duration</p>
              <p class="td-comparison-section__ideal-subtitle">${metaStr(meta.trip_duration)}</p>
            </div>
          </div>
          <p class="td-comparison-section__ideal-description">${metaStr(meta.trip_duration_byline)}</p>
        </div>
      </div>`;
    }

    _build_dimensions(meta) {
      const firstId = meta.variants_dimensions ? Object.keys(meta.variants_dimensions)[0] : null;
      const d = firstId ? meta.variants_dimensions[firstId] : {};
      return `<div class="td-comparison-section__dimensions-card" data-product-id="${meta.id}" data-variant-id="${firstId || ''}">
        <div class="td-comparison-section__dimensions-group">
          <div class="td-comparison-section__dimensions-block">
            <div class="td-comparison-section__unit-toggle" data-unit-toggle>
              <button type="button" class="td-comparison-section__unit-btn td-comparison-section__unit-btn--active" data-unit="imperial">Imperial</button>
              <button type="button" class="td-comparison-section__unit-btn" data-unit="metric">Metric</button>
            </div>
            <p class="td-comparison-section__dimensions-heading">Dimensions</p>
            <p class="td-comparison-section__dimensions-value" data-unit-imperial>${metaStr(d.imperial_dimensions)}</p>
            <p class="td-comparison-section__dimensions-value" data-unit-metric style="display:none;">${metaStr(d.metric_dimensions)}</p>
          </div>
          <div class="td-comparison-section__dimensions-block">
            ${this._getIcon('weight')}
            <p class="td-comparison-section__dimensions-heading">Weight</p>
            <p class="td-comparison-section__dimensions-value" data-unit-imperial>${metaStr(d.imperial_weight)}</p>
            <p class="td-comparison-section__dimensions-value" data-unit-metric style="display:none;">${metaStr(d.metric_weight)}</p>
          </div>
        </div>
      </div>`;
    }

    _build_video(meta) {
      const videoUrl = metaStr(meta.video_url);
      let videoId = '';
      if (videoUrl) {
        try {
          const url = new URL(videoUrl);
          videoId = url.searchParams.get('v') || url.pathname.split('/').pop() || '';
        } catch { videoId = videoUrl.split('=')[1] || ''; }
      }
      if (!videoId) {
        return `<div class="td-comparison-section__video-slot" data-product-id="${meta.id}"></div>`;
      }
      const poster = metaStr(meta.video_poster);
      const posterAlt = metaStr(meta.video_poster_alt) || metaStr(meta.title);
      const headline = metaStr(meta.video_headline);
      const linkText = metaStr(meta.video_link_text);
      const caption = metaStr(meta.video_caption);
      const posterHTML = poster
        ? `<img class="td-comparison-section__video-thumb" src="${poster}" alt="${posterAlt}" loading="lazy">`
        : '<div class="td-comparison-section__video-thumb td-comparison-section__video-thumb--placeholder"></div>';
      return `<div class="td-comparison-section__video-slot" data-product-id="${meta.id}">
        <button type="button" is="toggle-button" aria-controls="td-comparison-video-popup" aria-expanded="false" class="td-comparison-section__video-card video-modal-toggle" data-video-id="${videoId}" title="Play video">
          <div class="td-comparison-section__video-poster">
            ${posterHTML}
            <div class="td-comparison-section__video-overlay">
              ${headline ? `<p class="td-comparison-section__video-headline">${headline}</p>` : ''}
              <div class="td-comparison-section__video-play">
                ${this._getIcon('play')}
                ${linkText ? `<span class="td-comparison-section__video-link-text">${linkText}</span>` : ''}
              </div>
            </div>
          </div>
          ${caption ? `<p class="td-comparison-section__video-caption">${caption}</p>` : ''}
        </button>
      </div>`;
    }

    _build_straps(meta) {
      const bl = this.labels.blocks.straps;
      const icon = this._getIcon('comparison-check');
      const tooltipIcon = this._getIcon('tooltip');
      const item = (name, has, label) => `<div class="td-comparison-strap-card__item">
        <div class="td-comparison-strap-card__checkbox ${has ? 'td-comparison-strap-card__checkbox--checked' : ''}" role="img" aria-label="${has ? 'Included' : 'Not included'}">${icon}</div>
        <div class="td-comparison-strap-card__label">
          <p class="td-comparison-strap-card__name">${name}</p>
          <p class="td-comparison-strap-card__detail">${has ? (label || '') : ''}</p>
        </div>
      </div>`;
      const tooltipId = `accessory-tooltip-${meta.id}`;
      const accessoryTooltipHtml = meta.accessory_strap_details ? `
            <span class="td-comparison-strap-card__tooltip-wrap">
              <button type="button" class="td-comparison-strap-card__tooltip-trigger" data-strap-tooltip-toggle aria-label="More info about accessory straps" aria-describedby="${tooltipId}">${tooltipIcon}</button>
              <div class="td-comparison-strap-card__tooltip" data-strap-tooltip role="tooltip" id="${tooltipId}">
                <div class="td-comparison-strap-card__tooltip-content">
                  ${(meta.accessory_tooltip_title || bl.accessory_tooltip_title) ? `<p class="td-comparison-strap-card__tooltip-title">${meta.accessory_tooltip_title || bl.accessory_tooltip_title}</p>` : ''}
                  <p class="td-comparison-strap-card__tooltip-text">${meta.accessory_strap_details}</p>
                </div>
                <div class="td-comparison-strap-card__tooltip-arrow"></div>
              </div>
            </span>` : '';
      const accessoryItem = `<div class="td-comparison-strap-card__item">
        <div class="td-comparison-strap-card__checkbox ${meta.accessory_strap ? 'td-comparison-strap-card__checkbox--checked' : ''}" role="img" aria-label="${meta.accessory_strap ? 'Included' : 'Not included'}">${icon}</div>
        <div class="td-comparison-strap-card__label">
          <div class="td-comparison-strap-card__name-with-tooltip">
            <p class="td-comparison-strap-card__name">${bl.accessory_name}</p>${accessoryTooltipHtml}
          </div>
          <p class="td-comparison-strap-card__detail">${meta.accessory_strap ? (meta.accessory_strap_copy || '') : ''}</p>
        </div>
      </div>`;
      return `<div class="td-comparison-strap-card" data-product-id="${meta.id}">
        <div class="td-comparison-strap-card__grid">
          <div class="td-comparison-strap-card__row">${item(bl.shoulder_name, meta.shoulder_strap, meta.shoulder_strap_copy || '')}${item(bl.sternum_name, meta.sternum_strap, meta.sternum_strap_copy || '')}</div>
          <div class="td-comparison-strap-card__row">${accessoryItem}${item(bl.hip_belt_name, meta.hip_belt, meta.hip_belt_strap_copy || '')}</div>
        </div>
      </div>`;
    }

    _build_features(meta) {
      const bl = this.labels.blocks.key_features;
      const item = (icon, title, val) => {
        const v = metaStr(val);
        if (!v) return '';
        return `<div class="td-comparison-features-card__item">
          ${this._getIcon(icon)}<p class="td-comparison-features-card__title">${title}</p>
          <p class="td-comparison-features-card__desc">${v}</p>
        </div>`;
      };
      return `<div class="td-comparison-features-card" data-product-id="${meta.id}">
        <div class="td-comparison-features-card__list">
          ${item('laptop', bl.tech_storage_label, meta.tech_storage)}
          ${item('pocket', bl.pockets_label, meta.pockets)}
          ${item('load-support', bl.load_support_label, meta.load_support)}
          ${item('zipper', bl.handles_zippers_label, meta.handles_zippers)}
        </div>
      </div>`;
    }

    _build_materials(meta) {
      const bl = this.labels.blocks.materials;
      const item = (title, val) => {
        const v = metaStr(val);
        if (!v) return '';
        return `<div class="td-comparison-materials-card__item">
          <p class="td-comparison-materials-card__title">${title}</p>
          <p class="td-comparison-materials-card__desc">${v}</p>
        </div>`;
      };
      return `<div class="td-comparison-materials-card" data-product-id="${meta.id}">
        <div class="td-comparison-materials-card__list">
          ${item(bl.exterior_label, meta.exterior_material)}${item(bl.interior_label, meta.interior_material)}
          ${item(bl.coating_label, meta.coating_material)}${item(bl.zippers_label, meta.zippers_material)}
        </div>
      </div>`;
    }

    _build_compatible(meta) {
      const items = meta.compatible_products || [];
      const listHTML = items.map(p => {
        const ratingHTML = p.rating ? `
          <div class="td-comparison-compatible-card__rating">
            <div class="td-comparison-compatible-card__stars">${this._buildStarsHTML(p.rating)}</div>
            <p class="td-comparison-compatible-card__review-text">
              <span class="td-comparison-compatible-card__review-score">${p.rating}</span>
              ${p.review_count || 0} reviews
            </p>
          </div>` : '';
        return `<a href="${p.url}" class="td-comparison-compatible-card__item">
          ${p.image ? `<img class="td-comparison-compatible-card__image" src="${p.image}" alt="${p.title}" width="64" height="64" loading="lazy">` : '<div class="td-comparison-compatible-card__image"></div>'}
          <div class="td-comparison-compatible-card__info">
            ${ratingHTML}
            <p class="td-comparison-compatible-card__name">${p.title}</p>
            <p class="td-comparison-compatible-card__price">${formatMoney(p.price)}</p>
          </div>
        </a>`;
      }).join('');
      return `<div class="td-comparison-compatible-card" data-product-id="${meta.id}">
        <div class="td-comparison-compatible-card__list">${listHTML}</div>
      </div>`;
    }

    _buildStarsHTML(rating) {
      const full = this._getIcon('rating-star');
      const half = this._getIcon('rating-star-half');
      const empty = this._getIcon('rating-star-empty');
      let html = '';
      const decimal = rating % 1;
      let usedHalf = false;
      for (let i = 1; i <= 5; i++) {
        if (rating >= i) { html += full; }
        else if (!usedHalf && decimal >= 0.3 && decimal <= 0.7) { html += half; usedHalf = true; }
        else if (!usedHalf && decimal > 0.7) { html += full; usedHalf = true; }
        else { html += empty; }
      }
      return html;
    }
  }

  customElements.define('td-comparison-page', TDComparisonPage);
}
